import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { mutateData } from "@/core/api/api-methods";
import {
  invalidateAfterMutation,
  payrollBatchCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { payrollEndpoints } from "@/features/payroll/api/endpoints";
import type { PayrollBatchDto } from "@/features/payroll/api/dto";

/**
 * Retire un salarié d'une paie `DRAFT` ou `SIMULATED` (`DELETE
 * /PayrollBatches/{id}/lines/{lineId}`). La paie revient en `DRAFT`. Codes :
 * 200 ; 404 ; 422 (paie lancée).
 */
export const useDeletePayrollLine = (batchId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["payroll", "payrollBatches", "deleteLine", batchId],
    mutationFn: (lineId: string) =>
      mutateData({
        url: payrollEndpoints.payrollBatches.deleteLine(batchId, lineId),
        method: "DELETE",
      }) as unknown as Promise<PayrollBatchDto>,
    onSuccess: () =>
      invalidateAfterMutation(queryClient, payrollBatchCacheKeys(batchId)),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useDeletePayrollLine failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "payroll" }
      );
    },
  });
};
